import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Minus, ShieldCheck } from 'lucide-react';
import {
  AJUSTES_POR_DEFECTO,
  AJUSTES_VISIBLES,
  CLAVE_AJUSTE,
  leerAjusteGuardado,
  NOMBRE_ROL,
  ROLES,
  TAREAS_DEL_NEGOCIO,
  puedeUsarCanal,
  type AjustesPermisos,
  type Rol,
} from '@shared/permisos';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 64 — Quién puede hacer qué.
 *
 * Pedido del negocio: *"lo de los permisos se ve bien y se vería mejor si en el apartado de
 * CONFIGURACIÓN tuviera su UI/UX"*.
 *
 * Dos partes, y la diferencia entre ellas fue la decisión de diseño del sprint:
 *
 *  1. **La tabla es de sólo lectura.** No porque falte tiempo, sino porque una matriz
 *     editable de 157 acciones × 4 oficios es una superficie de error enorme: el día que
 *     alguien marque "la cocina puede cobrar" por curiosidad, nadie se entera hasta el
 *     corte. La tabla está para *responder* "¿por qué Juan no ve los reportes?", que es la
 *     pregunta real que llega por WhatsApp a las diez de la noche.
 *  2. **Tres interruptores curados**, con su consecuencia escrita al lado. Son las únicas
 *     tres decisiones que de verdad cambian de restaurante en restaurante.
 *
 * La tabla se pinta preguntando a `puedeUsarCanal` —la MISMA función que autoriza cada
 * llamada al servidor— así que no puede desmentir al sistema. Si algún día la matriz cambia
 * y esta pantalla no, la pantalla ya cambió: no hay una segunda copia que mantener.
 */

const GRUPOS = ['Atender', 'Cocina', 'Dinero', 'Existencias', 'El negocio'] as const;

export function PermisosSection() {
  const [ajustes, setAjustes] = useState<AjustesPermisos>(AJUSTES_POR_DEFECTO);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState<keyof AjustesPermisos | null>(null);
  const [mensaje, setMensaje] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);

  const cargar = useCallback(async () => {
    const claves = Object.keys(CLAVE_AJUSTE) as Array<keyof AjustesPermisos>;
    const leidos = await Promise.all(
      claves.map(async (clave) => {
        const res = await window.api.config.obtener(CLAVE_AJUSTE[clave]);
        // El mismo lector que el servidor. Sin valor guardado manda el default de fábrica.
        const valor = res.ok
          ? leerAjusteGuardado(res.data, AJUSTES_POR_DEFECTO[clave])
          : AJUSTES_POR_DEFECTO[clave];
        return [clave, valor] as const;
      }),
    );
    setAjustes(Object.fromEntries(leidos) as unknown as AjustesPermisos);
    setCargando(false);
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const cambiar = async (clave: keyof AjustesPermisos, valor: boolean) => {
    setGuardando(clave);
    setMensaje(null);
    const res = await window.api.config.actualizar(CLAVE_AJUSTE[clave], valor ? 'true' : 'false');
    setGuardando(null);
    if (!res.ok) {
      setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
      return;
    }
    setAjustes((prev) => ({ ...prev, [clave]: valor }));
    setMensaje({
      tipo: 'ok',
      texto: 'Listo. El cambio aplica de inmediato, sin necesidad de reiniciar.',
    });
  };

  /**
   * La tabla completa, calculada de una vez. Es la matriz real evaluada con los ajustes
   * que este negocio tiene puestos ahora — no la matriz "de fábrica".
   */
  const filas = useMemo(
    () =>
      TAREAS_DEL_NEGOCIO.map((t) => ({
        ...t,
        permitido: Object.fromEntries(
          ROLES.map((rol) => [rol, puedeUsarCanal(rol, t.canal, ajustes)]),
        ) as Record<Rol, boolean>,
      })),
    [ajustes],
  );

  if (cargando) return <div className="text-sm text-slate-500">Cargando permisos…</div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
          <div className="space-y-1 text-sm text-slate-600">
            <p className="font-medium text-slate-800">
              Cada quien ve sólo lo que necesita para su trabajo.
            </p>
            <p>
              Esta tabla es para consultar: te dice por qué alguien no encuentra un botón. Lo
              que sí puedes cambiar son los tres ajustes de abajo.
            </p>
          </div>
        </div>
      </div>

      {/* ── Los tres interruptores ── */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Ajustes de tu negocio</h2>
        {AJUSTES_VISIBLES.map((a) => (
          <div
            key={a.clave}
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="font-medium text-slate-800">{a.etiqueta}</div>
                <p className="text-sm text-slate-600">{a.consecuencia}</p>
                <p className="text-sm text-slate-500">{a.cuando}</p>
              </div>
              <Interruptor
                activo={ajustes[a.clave]}
                ocupado={guardando === a.clave}
                etiqueta={a.etiqueta}
                onCambiar={(v) => void cambiar(a.clave, v)}
              />
            </div>
          </div>
        ))}
        {mensaje && (
          <div
            role="status"
            className={`rounded-lg px-3 py-2 text-sm ${
              mensaje.tipo === 'ok'
                ? 'bg-emerald-50 text-emerald-800'
                : 'bg-rose-50 text-rose-800'
            }`}
          >
            {mensaje.texto}
          </div>
        )}
      </section>

      {/* ── La tabla ── */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Quién puede hacer qué</h2>
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Acción</th>
                {ROLES.map((rol) => (
                  <th key={rol} className="px-3 py-2 text-center font-semibold">
                    {NOMBRE_ROL[rol]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {GRUPOS.map((grupo) => {
                const delGrupo = filas.filter((f) => f.grupo === grupo);
                if (delGrupo.length === 0) return null;
                return (
                  <Fragment key={grupo}>
                    <tr className="bg-slate-50">
                      <td
                        colSpan={ROLES.length + 1}
                        className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"
                      >
                        {grupo}
                      </td>
                    </tr>
                    {delGrupo.map((f) => (
                      <tr key={f.canal} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-700">
                          {f.titulo}
                          {/*
                            Sprint 64 (auditoría) — Hay acciones que además del oficio piden
                            el código de 2FA del dueño (Sprint 32). Sin decirlo, la tabla
                            mostraba un ✓ cierto e incompleto, y el dueño se enteraba en el
                            piso de que su cajera no podía.
                          */}
                          {f.pideCodigo && (
                            <span
                              className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
                              title="Además del oficio, pide el código de seguridad del dueño"
                            >
                              + tu código
                            </span>
                          )}
                        </td>
                        {ROLES.map((rol) => (
                          <td key={rol} className="px-3 py-2 text-center">
                            <Celda permitido={f.permitido[rol]} rol={rol} tarea={f.titulo} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-sm text-slate-500">
          El dueño puede todo, siempre: es quien responde por el negocio. Cancelar una venta
          pide tu PIN aunque el oficio la tenga permitida, y lo marcado con{' '}
          <span className="rounded bg-amber-100 px-1 text-[11px] font-medium text-amber-800">
            + tu código
          </span>{' '}
          pide además tu código de seguridad, así que sólo lo puedes hacer tú.
        </p>
      </section>
    </div>
  );
}

/**
 * La palomita y el guion. Con `title` y texto para lector de pantalla, porque una tabla de
 * 26 × 4 símbolos sin etiquetas es ilegible para quien no ve los colores.
 */
function Celda({ permitido, rol, tarea }: { permitido: boolean; rol: Rol; tarea: string }) {
  const texto = permitido
    ? `${NOMBRE_ROL[rol]} sí puede: ${tarea}`
    : `${NOMBRE_ROL[rol]} no puede: ${tarea}`;
  return (
    <span title={texto}>
      <span className="sr-only">{texto}</span>
      {permitido ? (
        <Check className="mx-auto h-4 w-4 text-emerald-600" aria-hidden="true" />
      ) : (
        <Minus className="mx-auto h-4 w-4 text-slate-300" aria-hidden="true" />
      )}
    </span>
  );
}

/**
 * Interruptor de 44 px de alto: se opera con el pulgar sobre el mostrador, no con un mouse
 * en un escritorio.
 */
function Interruptor({
  activo,
  ocupado,
  etiqueta,
  onCambiar,
}: {
  activo: boolean;
  ocupado: boolean;
  etiqueta: string;
  onCambiar: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={activo}
      aria-label={etiqueta}
      disabled={ocupado}
      onClick={() => onCambiar(!activo)}
      className={`relative h-11 w-20 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
        activo ? 'bg-emerald-600' : 'bg-slate-300'
      }`}
    >
      <span
        className={`absolute top-1 h-9 w-9 rounded-full bg-white shadow transition-all ${
          activo ? 'left-10' : 'left-1'
        }`}
      />
      <span className="sr-only">{activo ? 'Activado' : 'Desactivado'}</span>
    </button>
  );
}
